import { createContext, useContext, useEffect, useState } from "react"
import { produce } from "immer"
import Swal from 'sweetalert2'
import { useAuth } from '../pages/AuthContext';

const GlobalContext = createContext()

export function GlobalProvider({ children }) {

	const { token } = useAuth();

	const [globalState, setGlobalState] = useState({
		"item_periodo": 0,
		"items": [],
			"docenteId": null,
			"periodoId": null
	})

	const updateGlobalState = (newState) => {
		setGlobalState((prev) => ({ ...prev, ...newState }))
	}

	const normalizarEstadoInstancia = (estadoRespuesta, estadoActual) => {
		const estadoCrudo = typeof estadoRespuesta === 'string'
			? estadoRespuesta
			: estadoRespuesta?.estado

		if (estadoCrudo) {
			return String(estadoCrudo).trim().toLowerCase()
		}

		return String(estadoActual || '').trim().toLowerCase()
	}

	const mostrarNotificacion = (timer, icon, title) => {
		const Toast = Swal.mixin({
			toast: true,
			position: "top-end",
			showConfirmButton: false,
			timer: timer,
			timerProgressBar: true,
			didOpen: (toast) => {
				toast.onmouseenter = Swal.stopTimer;
				toast.onmouseleave = Swal.resumeTimer;
			}
		})
		Toast.fire({
			icon: icon,
			title: title
		})
	}

	const guardarInstancia = async (idItemPeriodo, indice_completo, instancias) => {
		const docenteId = globalState.docenteId
		const periodoId = globalState.periodoId
		if (!docenteId || !periodoId) {
			return { error: 'No hay docente seleccionado para crear la instancia.' }
		}
		try {
			const response = await fetch(`${process.env.REACT_APP_API_URL}/item-instancias/create/${docenteId}/${periodoId}/`, {
				method: 'POST',
				headers: {
					"Content-Type": "application/json",
					"Authorization": `Bearer ${token}`
				},
				body: JSON.stringify({
					"item_periodo": idItemPeriodo,
					"atributos": instancias
				})
			});

			const res = await response.json();

			if (!response.ok) {
				const errorMessage = res?.msg || 'Error en la solicitud';
				throw new Error(errorMessage);
			}

			return res;

		} catch (error) {
			return { error: error.message };
		}
	}


	const borrarInstancia = async (instanciaID) => {
		const docenteId = globalState.docenteId
		const periodoId = globalState.periodoId
		if (!docenteId || !periodoId) {
			return null
		}
		try {
			const response = await fetch(`${process.env.REACT_APP_API_URL}/item-instancias/delete/${docenteId}/${instanciaID}/${periodoId}/`, {
				method: 'DELETE',
				headers: {
					"Content-Type": "application/json",
					"Authorization": `Bearer ${token}`
				},
			})
			if (!response.ok) {
				throw new Error('Error en la solicitud');
			}

			return response.status
		} catch (error) {
			return null
		}
	}

	const actualizarInstancia = async (instanciaID, instancias) => {
		const docenteId = globalState.docenteId
		const periodoId = globalState.periodoId
		if (!docenteId || !periodoId) {
			return { error: 'No hay docente seleccionado para actualizar la instancia.' }
		}
		try {
			const response = await fetch(`${process.env.REACT_APP_API_URL}/item-instancias/update/${docenteId}/${instanciaID}/${periodoId}/`, {
				method: 'PUT',
				headers: {
					"Content-Type": "application/json",
					"Authorization": `Bearer ${token}`
				},
				body: JSON.stringify({
					"atributos": instancias
				})
			})
			const res = await response.json();

			if (!response.ok) {
				const errorMessage = res?.msg || 'Error en la solicitud';
				throw new Error(errorMessage);
			}

			return res;

		} catch (error) {
			return { error: error.message };
		}
	}



	const globalStateAgregarInstancia = async (idItemPeriodo, indice_completo, instancias) => {
		const res = await guardarInstancia(idItemPeriodo, indice_completo, instancias)
		if (res && !res.error) {
			mostrarNotificacion(2000, "success", "Elemento creado correctamente");
			const atributosDesdeInstancia = instancias.map((atributo) => ({
				"id": atributo.id,
				"nombre": atributo.nombre
			}))

			const estadoRespuesta = typeof res.estado === "string" ? res.estado : res.estado?.estado

			setGlobalState((prev) => {
				const nuevoRegistroInstancia = {
					"id": res.id,
					"valores": instancias,
					"estado": String(estadoRespuesta || "creado_por_docente").trim().toLowerCase()
				}

				const itemExiste = prev.items.some(item => item.indice_completo === indice_completo)

				if (!itemExiste) {
					return {
						...prev,
						items: [
							...prev.items,
							{
								"id_item_periodo": idItemPeriodo,
								"indice_completo": indice_completo,
								"nombre": "",
								"atributos": atributosDesdeInstancia,
								"instancias": [nuevoRegistroInstancia]
							}
						]
					}
				}

				return {
					...prev,
					items: prev.items.map((item) => {
						if (item.indice_completo !== indice_completo) return item

						const atributosActualizados =
							!Array.isArray(item.atributos) || item.atributos.length === 0
								? atributosDesdeInstancia
								: item.atributos

						return {
							...item,
							atributos: atributosActualizados,
							instancias: [...(item.instancias || []), nuevoRegistroInstancia]
						}
					})
				}
			})
		} else {
			mostrarNotificacion(5000, "error", res?.error || "Ocurrió un error inesperado");
		}

		return res?.error
			? { success: false, error: res.error }
			: { success: true }
	}

	const globalStateActualizarInstancia = async (instanciaID, indice_completo, instancias) => {
		const res = await actualizarInstancia(instanciaID, instancias)
		if (res && !res.error) {
			mostrarNotificacion(2000, "success", "Elemento actualizado correctamente")
			const updatedState = produce(globalState, draft => {
				const item = draft.items.find(item => item.indice_completo === indice_completo)
				if (item) {
					const instancia = item.instancias.find(inst => inst.id === instanciaID)
					if (instancia) {
						instancia.valores = instancias
						instancia.estado = normalizarEstadoInstancia(res?.estado, instancia.estado)
					}
				}
			})
			updateGlobalState(updatedState)

		} else {
			mostrarNotificacion(5000, "error", res?.error || "Ocurrió un error inesperado");
		}

		return res?.error
			? { success: false, error: res.error }
			: { success: true }
	}


	const globalStateBorrarInstancia = async (indice_completo, instancia) => {
		const res = await borrarInstancia(instancia.id)
		if (res === 204) {
			mostrarNotificacion(2000, "success", "Elemento borrado correctamente")
			const updatedState = produce(globalState, draft => {
				const item = draft.items.find(item => item.indice_completo === indice_completo);
				if (item) {
					item.instancias = item.instancias.filter(itemInstancia => itemInstancia.id !== instancia.id)
				}
			})
			updateGlobalState(updatedState)
		} else {
			mostrarNotificacion(3000, "error", "Ocurrió un error al intentar borrar el elemento")
		}
	}

	const globalStateActualizarEstadoInstancia = async (instanciaID, indice_completo, instancias) => {
		const res = await actualizarInstancia(instanciaID, instancias)
		if (res && !res.error) {
			mostrarNotificacion(2000, "success", "Elemento actualizado correctamente")
			const updatedState = produce(globalState, draft => {
				const item = draft.items.find(item => item.indice_completo === indice_completo)
				if (item) {
					const instancia = item.instancias.find(inst => inst.id === instanciaID)
					if (instancia) {
						instancia.valores = instancias
						instancia.estado = normalizarEstadoInstancia(res?.estado, instancia.estado)
					}
				}
			})
			updateGlobalState(updatedState)

		} else {
			mostrarNotificacion(5000, "error", res?.error || "Ocurrió un error inesperado");
		}
	}	

	return (
		<GlobalContext.Provider value={{ globalState, updateGlobalState, setGlobalState, globalStateAgregarInstancia, globalStateBorrarInstancia, globalStateActualizarInstancia, globalStateActualizarEstadoInstancia }}>
			{children}
		</GlobalContext.Provider>
	)
}

export function useGlobal() {
	return useContext(GlobalContext);
}